import { DateTime } from '@ksyos/date-time';
import { Avatar, Badge, CollectionLink } from '@ksyos/design-system';
import { useNavigate } from '@ksyos/router';
import type { AccountRequestApiResult } from '@common/interfaces/api/accountRequestsApi';
import { AccountRequestStatus } from '@common/enums/accountRequestStatus';
import { formatString } from '@common/utils/formatString';
import { getDisplayInitials } from '@common/utils/getDisplayInitials';
import { getDisplayName } from '@common/utils/getDisplayName';
import { getSelectedLanguage } from '@frontend/translate';
import { PendingAccountRequestBadge } from './PendingAccountRequestBadge';

interface AccountRequestsCollectionLinkProps {
    accountRequest: AccountRequestApiResult;
    avatarSize?: number;
    padding?: 'small' | 'medium';
}

export function AccountRequestsCollectionLink(props: Readonly<AccountRequestsCollectionLinkProps>) {
    const language = getSelectedLanguage();
    const navigate = useNavigate();

    return (
        <CollectionLink
            id={props.accountRequest.id}
            key={props.accountRequest.id}
            mediaItem={
                <Avatar
                    initials={getDisplayInitials(props.accountRequest.userData.practitioner)}
                    size={props.avatarSize}
                />
            }
            onClick={() => navigate(`/accountRequest/${props.accountRequest.id}`)}
            padding={props.padding}
            subtitle={language.professionEnum[props.accountRequest.userData.employment.profession]}
            title={getDisplayName(props.accountRequest.userData.practitioner, false, 'nameGivenNameFamily')}
        >
            {props.accountRequest.status === AccountRequestStatus.pending &&
                (props.accountRequest.hasMatchedOnNameAndGender ? (
                    <Badge
                        size="medium"
                        text={language.organization.reviewMatch}
                    />
                ) : (
                    <PendingAccountRequestBadge approverPractitioner={props.accountRequest.approverPractitioner} />
                ))}

            {props.accountRequest.status === AccountRequestStatus.rejected && (
                <Badge
                    size="medium"
                    text={formatString(language.organization.rejectedAccountRequestAt, [
                        DateTime.from(props.accountRequest.updated_at).format('DD-MM-YYYY'),
                    ])}
                />
            )}
        </CollectionLink>
    );
}
